import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { get, patch, post } from "../../helpers/axios";
import {
  SECTIONS_API,
  CATEGORIES_API,
  QUESTIONS_API,
  CSF_ANSWER_SUBMIT_API,
  AUDIT_TRAIL_API,
} from "../../helpers/url_helpers";

// Async thunks
export const fetchSections = createAsyncThunk(
  "CSFSurvey/fetchSections",
  async ({ categoryId, fromSubmission = false }, { rejectWithValue }) => {
    try {
      const response = await get(`${SECTIONS_API}?category_id=${categoryId}`);
      return { data: response.data, fromSubmission };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCategories = createAsyncThunk(
  "CSFSurvey/fetchCategories",
  async (preSubId, { rejectWithValue }) => {
    try {
      const response = await get(`${CATEGORIES_API}?pre_sub_id=${preSubId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchQuestions = createAsyncThunk(
  "CSFSurvey/fetchQuestions",
  async (
    { sectionId, preSubId, fromSubmission = false },
    { rejectWithValue }
  ) => {
    try {
      const response = await get(
        `${QUESTIONS_API}${sectionId}/?pre_sub_id=${preSubId}`
      );
      return { data: response.data, fromSubmission };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const submitCSFAnswer = createAsyncThunk(
  "CSFSurvey/submitCSFAnswer",
  async ({ answerPayload, isEdit }, { rejectWithValue }) => {
    try {
      const response = isEdit
        ? await patch(CSF_ANSWER_SUBMIT_API, answerPayload)
        : await post(CSF_ANSWER_SUBMIT_API, answerPayload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAuditTrail = createAsyncThunk(
  "CSFSurvey/fetchAuditTrail",
  async ({ preSubId, qnId }, { rejectWithValue }) => {
    try {
      const response = await get(
        `${AUDIT_TRAIL_API}?pre_sub_id=${preSubId}&question_id=${qnId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  selectedCategory: {},
  selectedSection: {},
  selectedQuestion: {},
  sections: [],
  categories: [],
  questionData: [],
  surveyError: "",
  questionStatus: {},
  goalInfo: {},
  isPdfDownloading: false,
  isSurveyLoading: false,
  isCsvDownloading: false,
  auditData: [],
  loading: false,
  error: null,
  submissionStatus: {},
  isCSFComplete: false,
  submitLoading: false,
  auditLoading: false,
};

const CSFSurvey = createSlice({
  name: "CSFSurvey",
  initialState,
  reducers: {
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    setSelectedSection: (state, action) => {
      state.selectedSection = action.payload;
    },
    setSelectedQuestion: (state, action) => {
      state.selectedQuestion = action.payload;
    },
    clearCSFSurvey: (state) => {
      return {
        ...initialState,
        isCSFComplete: state.isCSFComplete, // preserve this
      };
    },
    setAssessmentComplete: (state, action) => {
      state.isCSFComplete = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Sections
      .addCase(fetchSections.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        if (!action.meta.arg?.fromSubmission) {
          state.sections = [];
        }
      })
      .addCase(fetchSections.fulfilled, (state, action) => {
        state.loading = false;
        state.sections = action.payload.data;

        if (!action.payload.fromSubmission) {
          // autoselect only if api is not called after submit
          state.selectedSection = action.payload.data[0];
        }
      })

      .addCase(fetchSections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
        state.selectedCategory = action.payload[0];
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Questions
      .addCase(fetchQuestions.pending, (state, action) => {
        state.loading = true;
        state.error = null;

        if (!action.meta.arg?.fromSubmission) {
          state.questionData = [];
        }
      })
      .addCase(fetchQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.questionData = action.payload.data;
        state.selectedQuestion =
          action.payload?.data?.section?.questions?.[0]?.id ?? null;
      })
      .addCase(fetchQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //answer submit
      .addCase(submitCSFAnswer.pending, (state) => {
        state.submitLoading = true;
        state.error = null;
      })
      .addCase(submitCSFAnswer.fulfilled, (state, action) => {
        state.submitLoading = false;
        state.submissionStatus = action.payload;
      })
      .addCase(submitCSFAnswer.rejected, (state, action) => {
        state.submitLoading = false;
        state.error = action.payload;
      })

      // Audit trail
      .addCase(fetchAuditTrail.pending, (state) => {
        state.auditLoading = true;
        state.error = null;
      })
      .addCase(fetchAuditTrail.fulfilled, (state, action) => {
        state.auditLoading = false;
        state.auditData = action.payload;
      })
      .addCase(fetchAuditTrail.rejected, (state, action) => {
        state.auditLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedCategory,
  setSelectedSection,
  setSelectedQuestion,
  clearCSFSurvey,
  setAssessmentComplete,
} = CSFSurvey.actions;

export default CSFSurvey.reducer;
